/* eslint-disable no-unused-vars */
import { makeStyles } from '@material-ui/core/styles';
import { Handle, Position } from '@xyflow/react';
import classnames from 'classnames';
import hexToRgba from 'hex-rgba';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../app/theme';
import { ModalType, NodeType } from '../../app/types';
import { changeSeconds } from '../../app/utils';
import IconClock from '../../assets/images/Icons/IconClock';
import icDelete20 from '../../assets/images/svg/ic-delete-20.svg';
import {
  checkNode,
  deleteNode,
  setDefaultTemplate,
  setIsShowSettingBar,
  setSelectedNode,
} from '../../features/mainSlice';
import { openModal } from '../../features/modalSlice';

const useStyles = makeStyles(
  {
    root: {
      width: 240,
      borderRadius: '12px',
      boxShadow: '0 8px 36px 0 rgba(0, 0, 0, 0.06)',
      border: 0,
    },
    selected: {
      border: `solid 1px ${color.$24}`,
    },
    error: {
      border: `solid 1px ${color.$17}`,
    },
    highlightPath: {
      border: `solid 1px ${color.$3}!important`,
    },
    handle: {
      border: '0 !important',
      borderRadius: '0 !important',
      background: `${color.$3} !important`,
      width: '1px !important',
      height: '1px !important',
      minWidth: '1px !important',
      minHeight: '1px !important',
    },
    title: {
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
      fontSize: '13px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 14px 8px 16px',
      background: hexToRgba(color.$24, 6),
      '& div': {
        display: 'flex',
        alignItems: 'center',
      },
      '& > svg': {
        marginRight: 2,
      },
    },
    errorTitle: {
      color: color.$17,
      background: hexToRgba(color.$17, 6),
    },
    content: {
      padding: '12px 16px',
    },
    value: {
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1.38',
      letterSpacing: 'normal',
      color: color.$3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '6px 0',
      background: color.$19,
      minHeight: 25,
    },
    iconInbox: {
      marginRight: 4,
    },
  },
  {
    name: 'Wait',
  },
);

const WaitComponent = ({ id, data, isConnectable }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isErrorNodeIds = useSelector(store => store.ma.isErrorNodeIds);
  const selectedNode = useSelector(store => store.ma.selectedNode);
  const isActive = useSelector(store => store.ma.isActive);
  const nodes = useSelector(store => store.ma.data?.nodes) || [];
  const allPathNodes = useSelector(store => store.ma.highlightPathNodes) || [];
  const isPreview = useSelector(store => store.ma.isPreview);
  const isHighlightPath = isPreview && !!allPathNodes.find(n => n?.id === id);
  const { waitTime } = data || {};
  const [isHover, setIsHover] = useState(false);

  const isSelected = selectedNode?.id === id;
  const isError =
    !!~isErrorNodeIds.indexOf(id) && !checkNode({ type: NodeType.WAIT, data });

  const waitText = useMemo(() => {
    const year = changeSeconds(waitTime, 'year');
    const month = changeSeconds(waitTime, 'month');
    const day = changeSeconds(waitTime, 'day');
    const hour = changeSeconds(waitTime, 'hour');
    const minute = changeSeconds(waitTime, 'minute');
    const second = changeSeconds(waitTime, 'second');

    if (waitTime === 0) {
      return t('message-node.not-wait');
    }

    return `${year ? `${t('message-node.year', { year })}` : ''}${
      month ? `${t('message-node.month', { month })}` : ''
    }${day ? `${t('message-node.day', { day })}` : ''}${
      hour ? `${t('message-node.hour', { hour })}` : ''
    }${minute ? `${t('message-node.minute', { minute })}` : ''}${
      second ? `${t('message-node.second', { second })}` : ''
    }`;
  }, [waitTime]);

  const onSelect = () => {
    if (!isSelected) {
      dispatch(setSelectedNode(nodes.find(n => n?.id === id)));
      dispatch(
        setDefaultTemplate({
          isShow: false,
        }),
      );
    }

    dispatch(setIsShowSettingBar(true));
  };

  function onDelete() {
    dispatch(
      openModal({
        modalType: ModalType.DIALOG_TEXT,
        modalProps: {
          title: t('delete-wait-node-modal.title'),
          content: t('delete-wait-node-modal.content'),
          cancelText: t('delete-wait-node-modal.cancelText'),
          submitText: t('delete-wait-node-modal.submitText'),
        },
        onClick: () => {
          dispatch(deleteNode(id));
        },
      }),
    );
  }

  return (
    <div>
      <Handle
        className={classes.handle}
        type="target"
        position={Position.Top}
        style={{ top: 0 }}
        isConnectable={isConnectable}
      />
      <div
        className={classnames(classes.root, {
          [classes.selected]: isSelected,
          [classes.error]: isError,
          [classes.highlightPath]: isHighlightPath,
        })}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        onClick={onSelect}
      >
        <div
          className={classnames(classes.title, {
            [classes.errorTitle]: isError,
          })}
        >
          <div>
            <IconClock
              className={classes.iconInbox}
              backgroundColor={isError ? color.$17 : color.$24}
            />
            {t('wait')}
          </div>
          {isHover && !isActive && (
            <img
              style={{ cursor: 'pointer' }}
              onClick={() => onDelete()}
              src={icDelete20}
              alt="delete"
            />
          )}
        </div>
        <div className={classes.content}>
          <div className={classes.value}>{waitText}</div>
        </div>
      </div>
      <Handle
        className={classes.handle}
        type="source"
        position={Position.Bottom}
        style={{ bottom: 0 }}
        isConnectable={isConnectable}
      />
    </div>
  );
};

export default WaitComponent;
